import { Link } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Button } from "../components/ui/button";
import { useSeo } from "../lib/seo";

const FREE = [
  "All 5 profit & marketplace calculators",
  "Instant results, no sign-up needed",
  "Scenario analysis on every calculator",
  "Platform compare (Meesho vs Amazon vs Flipkart)",
  "Save products to your account",
  "Catalogue view with best/worst products",
];

const PRO = [
  "Everything in Free",
  "Catalogue analytics & trends",
  "Bulk import & CSV export",
  "Price & margin alerts",
  "Advanced scenario planning",
  "Priority support",
];

export default function Pricing() {
  useSeo({
    title: "Pricing — Free Calculators, Pro Coming Soon | ProfitMigo",
    description: "ProfitMigo's calculators are free forever. A Pro plan with catalogue analytics, exports and alerts is coming soon.",
    path: "/pricing",
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Pricing" }]} />
      <header className="max-w-2xl">
        <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">Simple, honest pricing</h1>
        <p className="mt-4 text-lg text-muted-foreground">
          The calculators are free and stay free — they're how you find us. A Pro plan for tracking your whole catalogue is on the way.
        </p>
      </header>

      <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Free */}
        <div className="flex flex-col rounded-2xl border border-border bg-card p-8" data-testid="plan-free">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Free</div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="font-display text-4xl font-bold">₹0</span>
            <span className="text-muted-foreground">/ forever</span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">Everything you need to price a product and know your real profit.</p>
          <ul className="mt-6 space-y-3 text-sm">
            {FREE.map((f) => (
              <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{f}</li>
            ))}
          </ul>
          <Button asChild className="mt-8" data-testid="free-cta">
            <Link to="/ecommerce/product-profit-calculator">Start calculating</Link>
          </Button>
        </div>

        {/* Pro (coming soon) */}
        <div className="relative flex flex-col rounded-2xl border-2 border-primary/30 bg-accent/30 p-8" data-testid="plan-pro">
          <div className="absolute right-6 top-6 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
            Coming soon
          </div>
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
            <Sparkles className="h-3.5 w-3.5" /> Pro
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="font-display text-4xl font-bold text-muted-foreground">—</span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">Track and optimise the profitability of your entire catalogue.</p>
          <ul className="mt-6 space-y-3 text-sm">
            {PRO.map((f) => (
              <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{f}</li>
            ))}
          </ul>
          <Button className="mt-8" variant="secondary" disabled data-testid="pro-cta">Not yet available</Button>
          <p className="mt-3 text-center text-xs text-muted-foreground">Pro is not currently purchasable. No payment is taken.</p>
        </div>
      </div>

      <p className="mt-8 text-sm text-muted-foreground">
        We don't make fake pricing claims. Pro features and price will be finalised before it launches.
      </p>
    </div>
  );
}
